import * as React from "react"
import { cn } from "@/lib/utils"

interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode
  description?: React.ReactNode
  /** Rendered to the left of the title (icon, favorite button, avatar, …). */
  leading?: React.ReactNode
  /** Rendered next to the title — typically status badges. */
  badges?: React.ReactNode
  /** Action buttons rendered on the trailing side. */
  actions?: React.ReactNode
  /** Show a thin bottom rule under the header. */
  divider?: boolean
}

/**
 * PageHeader is the consistent block at the top of every list / detail page.
 *
 *   <PageHeader
 *     title={t('case_list')}
 *     description={t('case_list_desc')}
 *     actions={<CasesPrimaryButtons />}
 *   />
 */
export function PageHeader({
  title,
  description,
  leading,
  badges,
  actions,
  divider,
  className,
  ...props
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between",
        divider && "pb-4 border-b border-border/60",
        className
      )}
      {...props}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {leading}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground truncate">
              {title}
            </h1>
            {badges}
          </div>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-2 shrink-0">{actions}</div>
      )}
    </div>
  )
}

PageHeader.displayName = "PageHeader"
